import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Auth0ClientService } from '@auth0/auth0-angular';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { from, of } from 'rxjs';
import { concatMap, map, mergeMap } from 'rxjs/operators';

import { logger } from '@assess/shared/logger';

import { AuthenticationConfig } from '../types/authentication-config.types';

/**
 * Class that intercepts all HttpClient requests in order to add:
 *  - an `X-Identity-Token` header containing the user's identity token in order to add identifying information about
 *    the user
 * @private
 */
@Injectable()
export class AuthenticationTokenInterceptor implements HttpInterceptor {
  private readonly instanceLogger = logger.child({ context: AuthenticationTokenInterceptor.name });

  constructor(
    private readonly config: AuthenticationConfig,
    @Inject(Auth0ClientService) private auth0Client: Auth0Client
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const allowedList = this.config.identityToken.allowedList ?? [];

    if (!allowedList.some((entry) => req.url.startsWith(entry))) {
      return next.handle(req);
    }

    return this.getIdentityToken().pipe(
      mergeMap((identityToken) => {
        if (identityToken?.trim()) {
          return next.handle(req.clone({ setHeaders: { [this.config.identityToken.header]: identityToken } }));
        }

        // Although the request requires the identity token, allow the request to continue without the header so that
        // the API returns an error and the user might see a notification.
        return next.handle(req);
      })
    );
  }

  private getIdentityToken() {
    return from(this.auth0Client.getIdTokenClaims()).pipe(
      concatMap((claims) => {
        if (claims?.__raw) {
          return of(claims);
        }

        this.instanceLogger.warn('Identity token is nil; attempting refresh');

        return from(this.auth0Client.checkSession()).pipe(concatMap(() => this.auth0Client.getIdTokenClaims()));
      }),
      map((claims) => claims?.__raw)
    );
  }
}
